import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CalendarDays, TrendingUp } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import RevenueChart from '../../components/RevenueChart.jsx'
import RadarChart from '../../components/RadarChart.jsx'
import {
  TH_MON_ABBR, TH_MON_FULL, DAILY_LIMIT, GREEN, SLATE, ORANGE, PURPLE, PALETTE, RADAR_PRESETS,
  pad, toISO, daysBetween, fmt, enumMonths, bucketsFor, presetRange, comparePrevRange, compareLabel, comparePrevLabel, asList,
  Card, Segmented, Toggle, PresetBar, DateRange, FilterChips, Stat, Delta, ChartLoading, Notice, todayISO,
} from './statShared.jsx'

// Upsale Performance for one campaign (จัดฟัน). The BASE graph is the new-ortho-
// patient count — but counted by start_date (the newcase endpoints), since upsale
// happens around the treatment start, not patient registration. On top of that we
// overlay the two upsale campaigns, read from the revenue endpoints which carry a
// `count` field when filtered by type:
//   • Lock-in    (type=ortho_lockin)  — advance-pay the ortho fee, get a gift box
//   • ByeBye     (type=ortho_byebye)  — installment whitening bundled with ortho
// Channel (ช่องทาง) split and the ติดเครื่องมือ overlay are intentionally dropped.

const LOCKIN = PURPLE
const BYEBYE = ORANGE

// a Stat chip that toggles its series on click (dims to 35% when hidden)
function ToggleStat({ label, value, color, off, onClick }) {
  return (
    <button onClick={onClick} className={`text-left transition-opacity ${off ? 'opacity-35' : ''}`}>
      <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
        <span className="w-2 h-2 rounded-full" style={{ background: color }} />{label}
      </div>
      <div className="text-[19px] font-semibold text-slate-900 tabular-nums leading-tight">{value}</div>
    </button>
  )
}
const BOTTOM_START = '2024-06-01'      // newcase data starts here
const TOOTHTAXI_BRANCH = '9'           // HR leads, not ad-driven → off by default

// appointment (newcase) rows carry y/m/d; revenue rows carry `date` (day) or y/m (month)
const apptKey = (gran, r) => (gran === 'month' ? `${r.year}-${pad(r.month)}` : `${r.year}-${pad(r.month)}-${pad(r.day)}`)
// upsale rows are always fetched DAILY (the monthly revenue endpoint omits `count`
// for these promo types), so bucket the daily rows by gran client-side. Be robust
// to row shape — derive YYYY-MM-DD from `date` (ISO string) OR year/month/day.
function isoOf(r) {
  if (r == null) return null
  if (r.date != null && r.date !== '') {
    const s = String(r.date)
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  }
  if (r.year) return `${r.year}-${pad(r.month || 1)}-${pad(r.day || 1)}`
  return null
}
const revKey = (gran, r) => {
  const iso = isoOf(r)
  if (!iso) return '__nodate__'
  return gran === 'month' ? iso.slice(0, 7) : iso
}

export default function UpsaleStat({ campaign }) {
  const campaignStat = campaign?.stat || 'ortho'
  const today = useMemo(() => todayISO(), [])  // Bangkok, not the browser clock
  const [branchName, setBranchName] = useState({})

  const pfx = `stat:upsale:${campaignStat}`
  const init = useMemo(() => presetRange('1mo'), [])
  const [start, setStart] = usePersistedState(`${pfx}:start`, init[0])
  const [end, setEnd] = usePersistedState(`${pfx}:end`, init[1])
  const [preset, setPreset] = usePersistedState(`${pfx}:preset`, '1mo')
  const [mode, setMode] = usePersistedState(`${pfx}:mode`, 'sum')            // sum | branch | compare
  const [radarCompare, setRadarCompare] = usePersistedState(`${pfx}:radarCompare`, false)  // star-chart compare toggle
  const [radarOff, setRadarOff] = usePersistedState(`${pfx}:radarOff`, () => new Set())     // hidden radar polygons
  const [bottomOff, setBottomOff] = usePersistedState(`${pfx}:bottomOff`, () => new Set())   // hidden all-time lines
  const [branchOff, setBranchOff] = usePersistedState(`${pfx}:branchOff`, () => new Set([TOOTHTAXI_BRANCH]))

  // base (new ortho patients, start_date based)
  const [topRows, setTopRows] = useState(null)
  const [topState, setTopState] = useState({ loading: true, err: '' })
  const [loadedGran, setLoadedGran] = useState(null)
  const [cmpRows, setCmpRows] = useState(null)
  const [cmpState, setCmpState] = useState({ loading: false, err: '' })
  const [cmpLoadedGran, setCmpLoadedGran] = useState(null)
  const [monthRows, setMonthRows] = useState(null)
  const [monthState, setMonthState] = useState({ loading: true, err: '' })

  // upsale overlays (count via revenue endpoints)
  const [lockTop, setLockTop] = useState(null)
  const [byeTop, setByeTop] = useState(null)
  const [upTopState, setUpTopState] = useState({ loading: false, err: '' })
  const [upLoadedGran, setUpLoadedGran] = useState(null)
  const [lockMonth, setLockMonth] = useState(null)
  const [byeMonth, setByeMonth] = useState(null)
  const [upMonthState, setUpMonthState] = useState({ loading: false, err: '' })

  // upsale — previous period (star-chart compare only)
  const [lockCmp, setLockCmp] = useState(null)
  const [byeCmp, setByeCmp] = useState(null)
  const [upCmpState, setUpCmpState] = useState({ loading: false, err: '' })
  const [upCmpLoadedGran, setUpCmpLoadedGran] = useState(null)

  const rangeInvalid = end < start
  const rangeDays = daysBetween(start, end) + 1
  const gran = rangeDays > DAILY_LIMIT ? 'month' : 'day'
  const apptEp = gran === 'month' ? '/api/appointment/monthly' : '/api/appointment/daily'
  // Both upsale campaigns come from the revenue endpoint, differing only by type:
  // Lock-in = ortho_lockin, ByeBye Braces = ortho_byebye (both carry a `count`).
  // Always DAILY — the monthly endpoint doesn't return `count` for these types,
  // so we fetch daily and bucket to months client-side (these types are sparse).
  const revEp = '/api/revenue/daily'
  // short presets (เดือนนี้ / 1 เดือน) render a per-branch star chart of both
  // upsale campaigns; longer ranges render the time-series line chart.
  const isRadar = RADAR_PRESETS.includes(preset)
  // previous-period Lock-in/ByeBye are needed for BOTH the star compare toggle and
  // the line-chart เทียบปีก่อน (split) mode
  const needUpCmp = (isRadar && radarCompare) || (!isRadar && mode === 'compare')
  const topReady = topRows != null && loadedGran === gran
  const cmpReady = cmpRows != null && cmpLoadedGran === gran
  const upTopReady = lockTop != null && byeTop != null && upLoadedGran === gran
  const upCmpReady = lockCmp != null && byeCmp != null && upCmpLoadedGran === gran

  const applyPreset = (key) => { const [s, e] = presetRange(key); setStart(s); setEnd(e); setPreset(key) }

  useEffect(() => {
    cachedGet('/api/branches')
      .then((br) => setBranchName(Object.fromEntries(asList(br).map((b) => [String(b.id ?? b.branch_id), b.name || b.branch_name || `สาขา #${b.id}`]))))
      .catch(() => {})
  }, [])

  // base — top range
  useEffect(() => {
    if (rangeInvalid) return
    let alive = true
    setTopState({ loading: true, err: '' })
    apiGet(apptEp, { start_date: start, end_date: end, branch_id: 0, exclude_nhso: false })
      .then((rows) => { if (alive) { setTopRows(Array.isArray(rows) ? rows : []); setLoadedGran(gran); setTopState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setTopRows([]); setLoadedGran(gran); setTopState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [apptEp, start, end, rangeInvalid, gran])

  // base — all-time monthly
  useEffect(() => {
    let alive = true
    setMonthState({ loading: true, err: '' })
    apiGet('/api/appointment/monthly', { start_date: BOTTOM_START, end_date: today, branch_id: 0, exclude_nhso: false })
      .then((rows) => { if (alive) { setMonthRows(Array.isArray(rows) ? rows : []); setMonthState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setMonthRows([]); setMonthState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [today])

  // upsale — top range (lock-in + byebye, fetched together)
  useEffect(() => {
    if (rangeInvalid) return
    let alive = true
    setUpTopState({ loading: true, err: '' })
    Promise.all([
      apiGet(revEp, { start_date: start, end_date: end, branch_id: 0, exclude_nhso: false, type: 'ortho_lockin' }),
      apiGet(revEp, { start_date: start, end_date: end, branch_id: 0, exclude_nhso: false, type: 'ortho_byebye' }),
    ])
      .then(([l, b]) => { if (alive) { setLockTop(Array.isArray(l) ? l : []); setByeTop(Array.isArray(b) ? b : []); setUpLoadedGran(gran); setUpTopState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setLockTop([]); setByeTop([]); setUpLoadedGran(gran); setUpTopState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [revEp, start, end, rangeInvalid, gran])

  // upsale — previous period (star compare toggle OR line เทียบปีก่อน mode)
  useEffect(() => {
    if (!needUpCmp || rangeInvalid) { setLockCmp(null); setByeCmp(null); return }
    let alive = true
    const [ps, pe] = comparePrevRange(preset, start, end)
    setUpCmpState({ loading: true, err: '' })
    Promise.all([
      apiGet(revEp, { start_date: ps, end_date: pe, branch_id: 0, exclude_nhso: false, type: 'ortho_lockin' }),
      apiGet(revEp, { start_date: ps, end_date: pe, branch_id: 0, exclude_nhso: false, type: 'ortho_byebye' }),
    ])
      .then(([l, b]) => { if (alive) { setLockCmp(Array.isArray(l) ? l : []); setByeCmp(Array.isArray(b) ? b : []); setUpCmpLoadedGran(gran); setUpCmpState({ loading: false, err: '' }) } })
      .catch(() => { if (alive) { setLockCmp([]); setByeCmp([]); setUpCmpLoadedGran(gran); setUpCmpState({ loading: false, err: '' }) } })
    return () => { alive = false }
  }, [needUpCmp, preset, revEp, start, end, rangeInvalid, gran])

  // upsale — all-time monthly
  useEffect(() => {
    let alive = true
    setUpMonthState({ loading: true, err: '' })
    Promise.all([
      apiGet('/api/revenue/daily', { start_date: BOTTOM_START, end_date: today, branch_id: 0, exclude_nhso: false, type: 'ortho_lockin' }),
      apiGet('/api/revenue/daily', { start_date: BOTTOM_START, end_date: today, branch_id: 0, exclude_nhso: false, type: 'ortho_byebye' }),
    ])
      .then(([l, b]) => { if (alive) { setLockMonth(Array.isArray(l) ? l : []); setByeMonth(Array.isArray(b) ? b : []); setUpMonthState({ loading: false, err: '' }) } })
      .catch(() => { if (alive) { setLockMonth([]); setByeMonth([]); setUpMonthState({ loading: false, err: '' }) } })
    return () => { alive = false }
  }, [today])

  // branch catalog (base rows + revenue rows so every branch shows up)
  const branches = useMemo(() => {
    const ids = new Set()
    for (const arr of [topRows, monthRows, cmpRows, lockTop, byeTop]) for (const r of arr || []) ids.add(String(r.branch))
    return [...ids].filter((id) => id && id !== '0').sort((a, b) => Number(a) - Number(b))
      .map((id, i) => ({ id, label: branchName[id] || `สาขา #${id}`, color: PALETTE[i % PALETTE.length] }))
  }, [topRows, monthRows, cmpRows, lockTop, byeTop, branchName])
  const branchEnabled = useMemo(() => new Set(branches.filter((b) => !branchOff.has(b.id)).map((b) => b.id)), [branches, branchOff])

  // base passes campaign-type filter + branch filter; bypass='branch' skips branch
  const basePass = (r, bypass) =>
    String(r.appointmentList) === campaignStat && (bypass === 'branch' || branchEnabled.has(String(r.branch)))
  // upsale counts are summed as a PLAIN TOTAL (no branch filter) — like the old
  // ติดเครื่องมือ overlay — so the line always reflects the true upsale total.
  // The per-branch star chart is the one place a row's branch is required.
  const branchHit = (r) => branchEnabled.has(String(r.branch))

  // bucket sums
  const baseByBucket = (rows, bypass) => {
    const acc = {}
    for (const r of rows || []) { if (!basePass(r, bypass)) continue; const k = apptKey(gran, r); acc[k] = (acc[k] || 0) + (r.count || 0) }
    return acc
  }
  // plain-total bucket sum; revenue rows (lock-in & byebye) key on r.date /
  // year-month — keyFn=revKey reduces them to the same bucket keys as the base
  const sumByBucket = (rows, keyFn) => {
    const acc = {}
    for (const r of rows || []) { const k = keyFn(gran, r); acc[k] = (acc[k] || 0) + (r.count || 0) }
    return acc
  }

  const baseSum = useMemo(() => (topRows || []).reduce((s, r) => (basePass(r, null) ? s + (r.count || 0) : s), 0), [topRows, branchEnabled])
  const lockSum = useMemo(() => (lockTop || []).reduce((s, r) => s + (r.count || 0), 0), [lockTop])
  const byeSum = useMemo(() => (byeTop || []).reduce((s, r) => s + (r.count || 0), 0), [byeTop])
  const lockRate = baseSum ? Math.round((lockSum / baseSum) * 100) : null
  const byeRate = baseSum ? Math.round((byeSum / baseSum) * 100) : null

  const top = useMemo(() => {
    if (rangeInvalid || !topReady) return { data: [], series: [] }
    const buckets = bucketsFor(gran, start, end)
    const lAcc = sumByBucket(lockTop, revKey), bAcc = sumByBucket(byeTop, revKey)

    if (mode === 'branch') {
      const shown = branches.filter((b) => branchEnabled.has(b.id))
      const series = shown.map((b) => ({ key: b.id, label: b.label, color: b.color }))
      const acc = {}
      for (const r of topRows) {
        if (!basePass(r, 'branch') || !branchEnabled.has(String(r.branch))) continue
        const k = apptKey(gran, r)
        ;(acc[k] || (acc[k] = {}))[String(r.branch)] = (acc[k][String(r.branch)] || 0) + (r.count || 0)
      }
      const data = buckets.map((bk) => { const row = { label: bk.label, full: bk.full }; for (const s of series) row[s.key] = acc[bk.key]?.[s.key] || 0; return row })
      return { data, series }
    }
    if (mode === 'compare') {
      const [ps, pe] = comparePrevRange(preset, start, end)
      const prevBuckets = bucketsFor(gran, ps, pe)
      const cur = baseByBucket(topRows, null), prv = baseByBucket(cmpReady ? cmpRows : null, null)
      const series = [
        { key: 'current', label: 'คนไข้ใหม่ (ช่วงนี้)', color: GREEN },
        { key: 'previous', label: `${comparePrevLabel(preset)} (คนไข้ใหม่)`, color: SLATE, dashed: true },
        { key: 'lockin', label: 'Lock-in', color: LOCKIN },
        { key: 'byebye', label: 'ByeBye Braces', color: BYEBYE },
      ]
      const data = buckets.map((bk, i) => ({
        label: bk.label, full: bk.full,
        current: cur[bk.key] || 0, previous: prv[prevBuckets[i]?.key] || 0,
        lockin: lAcc[bk.key] || 0, byebye: bAcc[bk.key] || 0,
      }))
      return { data, series }
    }
    const acc = baseByBucket(topRows, null)
    const series = [
      { key: 'value', label: 'คนไข้ใหม่จัดฟัน', color: GREEN },
      { key: 'lockin', label: 'Lock-in', color: LOCKIN },
      { key: 'byebye', label: 'ByeBye Braces', color: BYEBYE },
    ]
    const data = buckets.map((bk) => ({ label: bk.label, full: bk.full, value: acc[bk.key] || 0, lockin: lAcc[bk.key] || 0, byebye: bAcc[bk.key] || 0 }))
    return { data, series }
  }, [topRows, cmpRows, lockTop, byeTop, topReady, cmpReady, mode, gran, start, end, preset, rangeInvalid, branches, branchEnabled])

  // two star charts (one per campaign). Each plots the campaign count per branch
  // (solid) against a second polygon (dashed): the base คนไข้ใหม่จัดฟัน by default,
  // or the campaign's own previous period when the compare toggle is on.
  const radarData = useMemo(() => {
    const empty = { lock: [], bye: [], lockSum: 0, byeSum: 0, baseSum: 0, lockPrevSum: 0, byePrevSum: 0 }
    if (!isRadar || !upTopReady) return empty
    const base = {}, lk = {}, by = {}, lkP = {}, byP = {}
    for (const r of topRows || []) { if (!basePass(r, null)) continue; base[String(r.branch)] = (base[String(r.branch)] || 0) + (r.count || 0) }
    for (const r of lockTop || []) { if (!branchHit(r)) continue; lk[String(r.branch)] = (lk[String(r.branch)] || 0) + (r.count || 0) }
    for (const r of byeTop || []) { if (!branchHit(r)) continue; by[String(r.branch)] = (by[String(r.branch)] || 0) + (r.count || 0) }
    if (radarCompare) {
      for (const r of lockCmp || []) { if (!branchHit(r)) continue; lkP[String(r.branch)] = (lkP[String(r.branch)] || 0) + (r.count || 0) }
      for (const r of byeCmp || []) { if (!branchHit(r)) continue; byP[String(r.branch)] = (byP[String(r.branch)] || 0) + (r.count || 0) }
    }
    const enabled = branches.filter((br) => branchEnabled.has(br.id))
    const sum = (m) => enabled.reduce((s, br) => s + (m[br.id] || 0), 0)
    const axes = (cur, prv) => enabled
      .map((br) => ({ key: br.id, label: br.label, color: br.color, value: cur[br.id] || 0, prev: prv[br.id] || 0 }))
      .filter((a) => a.value > 0 || a.prev > 0)
    return {
      lock: axes(lk, radarCompare ? lkP : base),
      bye:  axes(by, radarCompare ? byP : base),
      lockSum: sum(lk), byeSum: sum(by), baseSum: sum(base), lockPrevSum: sum(lkP), byePrevSum: sum(byP),
    }
  }, [isRadar, upTopReady, topRows, lockTop, byeTop, lockCmp, byeCmp, radarCompare, branches, branchEnabled])

  const baseSummary = useMemo(() => {
    const total = (rows) => (rows || []).reduce((s, r) => (basePass(r, null) ? s + (r.count || 0) : s), 0)
    return { current: total(topRows) }
  }, [topRows, branchEnabled])

  // line แยกสาขา: each campaign broken out by branch (base hidden) — two charts
  const lineSplit = useMemo(() => {
    if (isRadar || rangeInvalid || !topReady || !upTopReady) return null
    const buckets = bucketsFor(gran, start, end)
    const shown = branches.filter((b) => branchEnabled.has(b.id))
    const series = shown.map((b) => ({ key: b.id, label: b.label, color: b.color }))
    const build = (rows) => {
      const acc = {}
      for (const r of rows || []) {
        if (!branchHit(r)) continue
        const k = revKey(gran, r), bid = String(r.branch)
        ;(acc[k] || (acc[k] = {}))[bid] = (acc[k][bid] || 0) + (r.count || 0)
      }
      return buckets.map((bk) => { const row = { label: bk.label, full: bk.full }; for (const s of series) row[s.key] = acc[bk.key]?.[s.key] || 0; return row })
    }
    return { series, lock: build(lockTop), bye: build(byeTop) }
  }, [isRadar, rangeInvalid, topReady, upTopReady, gran, start, end, branches, branchEnabled, lockTop, byeTop])

  // line เทียบปีก่อน: each campaign current-vs-previous, keeping คนไข้ใหม่จัดฟัน — two charts
  const lineCompareData = useMemo(() => {
    if (isRadar || rangeInvalid || !topReady || !upTopReady) return null
    const [ps, pe] = comparePrevRange(preset, start, end)
    const buckets = bucketsFor(gran, start, end)
    const prevBuckets = bucketsFor(gran, ps, pe)
    const baseAcc = baseByBucket(topRows, null)
    const mk = (curRows, prevRows, label, color) => {
      const curAcc = sumByBucket(curRows, revKey), prevAcc = sumByBucket(prevRows, revKey)
      const series = [
        { key: 'base', label: 'คนไข้ใหม่จัดฟัน', color: GREEN },
        { key: 'cur', label, color },
        { key: 'prev', label: `${comparePrevLabel(preset)} (${label})`, color: SLATE, dashed: true },
      ]
      const data = buckets.map((bk, i) => ({ label: bk.label, full: bk.full, base: baseAcc[bk.key] || 0, cur: curAcc[bk.key] || 0, prev: prevAcc[prevBuckets[i]?.key] || 0 }))
      return { data, series }
    }
    return { lock: mk(lockTop, lockCmp, 'Lock-in', LOCKIN), bye: mk(byeTop, byeCmp, 'ByeBye Braces', BYEBYE) }
  }, [isRadar, rangeInvalid, topReady, upTopReady, preset, gran, start, end, topRows, lockTop, byeTop, lockCmp, byeCmp, branchEnabled])

  const bottom = useMemo(() => {
    if (!monthRows) return []
    const acc = {}, l = {}, b = {}
    for (const r of monthRows) { if (r.year < 2000 || !basePass(r, null)) continue; const k = `${r.year}-${pad(r.month)}`; acc[k] = (acc[k] || 0) + (r.count || 0) }
    for (const r of lockMonth || []) { const iso = isoOf(r); if (!iso) continue; const k = iso.slice(0, 7); l[k] = (l[k] || 0) + (r.count || 0) }
    for (const r of byeMonth || []) { const iso = isoOf(r); if (!iso) continue; const k = iso.slice(0, 7); b[k] = (b[k] || 0) + (r.count || 0) }
    return enumMonths(BOTTOM_START, today).map(({ year, month }) => {
      const k = `${year}-${pad(month)}`
      return { label: `${TH_MON_ABBR[month - 1]} ${String(year).slice(2)}`, full: `${TH_MON_FULL[month - 1]} ${year}`, value: acc[k] || 0, lockin: l[k] || 0, byebye: b[k] || 0 }
    })
  }, [monthRows, lockMonth, byeMonth, today, branchEnabled])
  const bottomSum = useMemo(() => bottom.reduce((a, d) => ({ value: a.value + d.value, lockin: a.lockin + d.lockin, byebye: a.byebye + d.byebye }), { value: 0, lockin: 0, byebye: 0 }), [bottom])

  const toggleBranch = (id) => setBranchOff((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const toggleRadar = (key) => setRadarOff((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })
  const toggleBottom = (key) => setBottomOff((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n })

  const bottomSeries = [
    { key: 'value', label: 'คนไข้ใหม่จัดฟัน', color: GREEN },
    { key: 'lockin', label: 'Lock-in', color: LOCKIN },
    { key: 'byebye', label: 'ByeBye Braces', color: BYEBYE },
  ]
  const bottomActive = bottomSeries.filter((s) => !bottomOff.has(s.key))

  return (
    <>
      <p className="text-[12.5px] text-slate-500 mb-3">
        ประสิทธิภาพการอัปเซล (นับตามวันเริ่มรักษา) · ฐาน = คนไข้จัดฟันใหม่ · Lock-in = จ่ายค่ารักษาล่วงหน้ารับกล่องของขวัญ · ByeBye Braces = ผ่อนฟอกสีฟันคู่จัดฟัน
      </p>

      <div className="mb-5">
        <FilterChips label="สาขา" items={branches} enabled={branchEnabled}
          onToggle={toggleBranch} onAll={() => setBranchOff(new Set())} onClear={() => setBranchOff(new Set(branches.map((b) => b.id)))} />
      </div>

      <div className="space-y-6">
        <Card
          icon={<TrendingUp size={18} strokeWidth={1.75} />}
          title="การอัปเซลตามช่วงเวลา"
          badge={isRadar ? 'เรดาร์รายสาขา' : gran === 'month' ? 'รายเดือน' : 'รายวัน'}
          subtitle={isRadar ? 'Lock-in และ ByeBye Braces แยกคนละกราฟ · เทียบกับคนไข้ใหม่จัดฟัน (หรือช่วงก่อนเมื่อเปิดเทียบ)' : gran === 'month' ? `ช่วงยาว (${rangeDays} วัน) — แสดงแบบรายเดือน` : 'แยกเป็นรายวัน'}
          right={<DateRange start={start} end={end} today={today} onStart={(v) => { setStart(v); setPreset('') }} onEnd={(v) => { setEnd(v); setPreset('') }} />}
        >
          <PresetBar preset={preset} onPick={applyPreset} />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {isRadar ? (
              <Toggle on={radarCompare} onClick={() => setRadarCompare((v) => !v)}>{compareLabel(preset)}</Toggle>
            ) : (
              <Segmented value={mode} onChange={setMode} options={[{ v: 'sum', label: 'รวม' }, { v: 'branch', label: 'แยกสาขา' }, { v: 'compare', label: compareLabel(preset) }]} />
            )}
          </div>

          {rangeInvalid ? (
            <Notice text="วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น" tone="warn" />
          ) : topState.loading || !topReady || upTopState.loading || !upTopReady || (needUpCmp && (upCmpState.loading || !upCmpReady)) ? (
            <ChartLoading />
          ) : topState.err ? (
            <Notice text={topState.err} tone="error" />
          ) : isRadar ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
              {/* Lock-in */}
              <div>
                <div className="flex flex-wrap items-end gap-x-6 gap-y-1 mb-1">
                  <ToggleStat label="Lock-in" value={fmt(radarData.lockSum)} color={LOCKIN}
                    off={radarOff.has('lockV')} onClick={() => toggleRadar('lockV')} />
                  <ToggleStat label={radarCompare ? comparePrevLabel(preset) : 'คนไข้ใหม่จัดฟัน'}
                    value={fmt(radarCompare ? radarData.lockPrevSum : radarData.baseSum)} color={radarCompare ? SLATE : GREEN}
                    off={radarOff.has('lockP')} onClick={() => toggleRadar('lockP')} />
                </div>
                <RadarChart axes={radarData.lock} color={LOCKIN} prevColor={radarCompare ? SLATE : GREEN} compare
                  showValue={!radarOff.has('lockV')} showPrev={!radarOff.has('lockP')} height={340} compact={false}
                  emptyText="ยังไม่มีข้อมูล Lock-in ในช่วงนี้" />
              </div>
              {/* ByeBye Braces */}
              <div>
                <div className="flex flex-wrap items-end gap-x-6 gap-y-1 mb-1">
                  <ToggleStat label="ByeBye Braces" value={fmt(radarData.byeSum)} color={BYEBYE}
                    off={radarOff.has('byeV')} onClick={() => toggleRadar('byeV')} />
                  <ToggleStat label={radarCompare ? comparePrevLabel(preset) : 'คนไข้ใหม่จัดฟัน'}
                    value={fmt(radarCompare ? radarData.byePrevSum : radarData.baseSum)} color={radarCompare ? SLATE : GREEN}
                    off={radarOff.has('byeP')} onClick={() => toggleRadar('byeP')} />
                </div>
                <RadarChart axes={radarData.bye} color={BYEBYE} prevColor={radarCompare ? SLATE : GREEN} compare
                  showValue={!radarOff.has('byeV')} showPrev={!radarOff.has('byeP')} height={340} compact={false}
                  emptyText="ยังไม่มีข้อมูล ByeBye ในช่วงนี้" />
              </div>
            </div>
          ) : mode === 'branch' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <div className="mb-2 text-[13px] font-semibold flex items-center gap-1.5" style={{ color: LOCKIN }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: LOCKIN }} />Lock-in <span className="text-slate-400 font-normal">รายสาขา · {fmt(lockSum)}</span>
                </div>
                <RevenueChart data={lineSplit?.lock || []} series={lineSplit?.series || []} height={300} compact={false} emptyText="ยังไม่มีข้อมูล Lock-in" />
              </div>
              <div>
                <div className="mb-2 text-[13px] font-semibold flex items-center gap-1.5" style={{ color: BYEBYE }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: BYEBYE }} />ByeBye Braces <span className="text-slate-400 font-normal">รายสาขา · {fmt(byeSum)}</span>
                </div>
                <RevenueChart data={lineSplit?.bye || []} series={lineSplit?.series || []} height={300} compact={false} emptyText="ยังไม่มีข้อมูล ByeBye" />
              </div>
            </div>
          ) : mode === 'compare' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
              <div>
                <div className="mb-2 text-[13px] font-semibold flex items-center gap-1.5" style={{ color: LOCKIN }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: LOCKIN }} />Lock-in <span className="text-slate-400 font-normal">{compareLabel(preset)}</span>
                </div>
                <RevenueChart data={lineCompareData?.lock.data || []} series={lineCompareData?.lock.series || []} height={300} compact={false} />
              </div>
              <div>
                <div className="mb-2 text-[13px] font-semibold flex items-center gap-1.5" style={{ color: BYEBYE }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: BYEBYE }} />ByeBye Braces <span className="text-slate-400 font-normal">{compareLabel(preset)}</span>
                </div>
                <RevenueChart data={lineCompareData?.bye.data || []} series={lineCompareData?.bye.series || []} height={300} compact={false} />
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                <Stat label="คนไข้ใหม่จัดฟัน" value={fmt(baseSummary.current)} color={GREEN} />
                <Stat label="Lock-in" value={fmt(lockSum)} color={LOCKIN} />
                <Stat label="ByeBye Braces" value={fmt(byeSum)} color={BYEBYE} />
                {lockRate != null && (
                  <span className="text-[12px] text-slate-400 pb-1">อัตราอัปเซล — Lock-in {lockRate}% · ByeBye {byeRate}%</span>
                )}
              </div>
              <RevenueChart data={top.data} series={top.series} height={310} compact={false} />
            </>
          )}
        </Card>

        <Card icon={<BarChart3 size={18} strokeWidth={1.75} />} title="การอัปเซลรายเดือน" subtitle="ทั้งหมด ตั้งแต่เริ่มเก็บข้อมูล (ตามตัวกรองสาขาด้านบน)">
          {monthState.loading || upMonthState.loading ? (
            <ChartLoading />
          ) : monthState.err ? (
            <Notice text={monthState.err} tone="error" />
          ) : (
            <>
              <p className="text-[11.5px] text-slate-400 mb-2">คลิกที่ตัวเลขเพื่อแสดง/ซ่อนแต่ละเส้น</p>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                <ToggleStat label="คนไข้ใหม่จัดฟัน" value={fmt(bottomSum.value)} color={GREEN} off={bottomOff.has('value')} onClick={() => toggleBottom('value')} />
                <ToggleStat label="Lock-in" value={fmt(bottomSum.lockin)} color={LOCKIN} off={bottomOff.has('lockin')} onClick={() => toggleBottom('lockin')} />
                <ToggleStat label="ByeBye Braces" value={fmt(bottomSum.byebye)} color={BYEBYE} off={bottomOff.has('byebye')} onClick={() => toggleBottom('byebye')} />
                <span className="text-[12px] text-slate-400 pb-1">{bottom.length} เดือน</span>
              </div>
              <RevenueChart data={bottom} series={bottomActive.length ? bottomActive : bottomSeries} height={320} compact={false} labelAll valueLabels />
            </>
          )}
        </Card>
      </div>
    </>
  )
}
